import { useMemo } from 'react';
import { motion } from 'framer-motion';

interface ConfettiBurstProps {
  /** Toggle the burst. Re-mount with a key to replay. */
  fire: boolean;
  count?: number;
  className?: string;
}

const PALETTE = [
  '#1E88E5',
  '#3DAE5F',
  '#22D3EE',
  '#EC4899',
  '#FBBF24',
  '#A9D2EA',
];

/**
 * Lightweight confetti burst using framer-motion. No external deps.
 * Renders `count` colored squares that explode radially then fade.
 * Respects prefers-reduced-motion (renders nothing).
 */
export function ConfettiBurst({ fire, count = 28, className }: ConfettiBurstProps) {
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const pieces = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const dist = 80 + Math.random() * 90;
      return {
        id: i,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist - 30,
        rot: Math.random() * 360,
        color: PALETTE[i % PALETTE.length],
        size: 6 + Math.random() * 6,
        delay: Math.random() * 0.05,
      };
    });
  }, [count]);

  if (reduced || !fire) return null;

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 grid place-items-center overflow-visible ${className ?? ''}`}
    >
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0.6 }}
          animate={{
            x: p.x,
            y: p.y,
            rotate: p.rot,
            opacity: 0,
            scale: 1,
          }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: p.delay }}
          className="absolute rounded-[2px]"
          style={{
            width: p.size,
            height: p.size * 0.6,
            backgroundColor: p.color,
            boxShadow: `0 1px 2px rgba(6,18,36,0.18)`,
          }}
        />
      ))}
    </div>
  );
}

export default ConfettiBurst;
